import { useIntl } from '@/intl'
import { useState } from 'react'

export const useTexts = () => {
  const { translate } = useIntl()

  const [api] = useState(() => ({
    sms: translate({
      id: 'components.notificationEditor.smsTypeLabel',
    }),

    email: translate({
      id: 'components.notificationEditor.emailButton',
    }),

    bodyLabel: translate({
      id: 'sms4.settings.communication.notifications.form.templateCustom.label',
    }),

    etaRangeLabel: translate({
      id: 'sms4.settings.communication.notifications.form.windowOfCommitmentToleranceMins.label',
    }),

    etaRangeMinutes: (minutes: number) =>
      translate({
        id: 'sms4.settings.communication.notifications.form.windowOfCommitmentToleranceMins.value.minutes',
        values: { minutes },
      }),

    etaRangeHours: (hours: number) =>
      translate({
        id: 'sms4.settings.communication.notifications.form.windowOfCommitmentToleranceMins.value.hours',
        values: { hours },
      }),

    etaRangeHelperText: (etaTime, startTime, endTime) =>
      translate({
        id: 'sms4.settings.communication.notifications.form.windowOfCommitmentToleranceMins.helperText',
        values: { etaTime, startTime, endTime },
      }),

    backupNotificationLabel: translate({
      id: 'sms4.settings.communication.notifications.form.backupNotification.label',
    }),

    backupNotificationHelperText: translate({
      id: 'sms4.settings.communication.notifications.form.backupNotification.helperText',
    }),

    backupSubjectLabel: required =>
      `${translate({
        id: 'sms4.settings.communication.notifications.form.backupSubject.label',
      })}${required ? '*' : ''}`,

    notificationCutoffMinsLabel: `${translate({ id: 'sms4.settings.communication.notifications.form.notificationCutoffMins.label' })}*`,

    notificationCutoffMinsLabelHelperText: translate({
      id: 'sms4.settings.communication.notifications.form.notificationCutoffMins.helperText',
    }),

    notificationWayLabel: translate({
      id: 'sms4.settings.communication.notifications.form.notificationWay.label',
    }),

    subjectCustomLabel: required =>
      `${translate({
        id: 'sms4.settings.communication.notifications.form.emailSubject.label',
      })}${required ? '*' : ''}`,

    toleranceMinsLabel: `${translate({
      id: 'sms4.settings.communication.notifications.form.toleranceMins.label',
    })}*`,

    toleranceMinsHelperText: translate({
      id: 'sms4.settings.communication.notifications.form.proximityThresholdMins.helperText',
    }),

    toleranceMinsValue: (minutes?: number) =>
      minutes
        ? translate({
            id: 'sms4.settings.communication.notifications.form.toleranceMins.value',
            values: { minutes },
          })
        : translate({
            id: 'sms4.settings.communication.notifications.form.toleranceMins.label.noTolerance',
          }),

    proximityThresholdMinsLabel: translate({
      id: 'sms4.settings.communication.notifications.form.proximityThresholdMins.label',
    }),

    proximityThresholdMinsValue: (value: number) =>
      translate({
        id: 'global.plural.short.minutes',
        values: { value },
      }),

    trackingLinkLabel: translate({
      id: 'sms4.settings.communication.notifications.form.trackingLink.label',
    }),

    trackingLinkHelperText: translate({
      id: 'sms4.settings.communication.notifications.form.trackingLink.helperText',
    }),

    unsubscribeInstructionsLabel: translate({
      id: 'sms4.settings.communication.notifications.form.unsubscribeInstructions.label',
    }),

    unsubscribeInstructionsHelperText: translate({
      id: 'sms4.settings.communication.notifications.form.unsubscribeInstructions.helperText',
    }),

    unsubscribeInstructionsAlertTitle: translate({
      id: 'settings.customerNotifications.serviceDay.includeUnSubscriptionInstructions.card.title',
    }),

    unsubscribeInstructionsAlertTextEmail: translate({
      id: 'settings.customerNotifications.serviceDay.includeUnSubscriptionInstructions.card.text.email',
    }),

    unsubscribeInstructionsAlertTextPhone: translate({
      id: 'settings.customerNotifications.serviceDay.includeUnSubscriptionInstructions.card.text.sms',
    }),

    smsMessageLabel: `${translate({
      id: 'sms4.settings.communication.notifications.form.message.sms.label',
    })}*`,

    emailMessageLabel: `${translate({
      id: 'sms4.settings.communication.notifications.form.message.emailText.label',
    })}*`,

    smsEnableConditionMessage:
      'sms4.settings.communication.notifications.form.smsEnableConditionMessage',

    requiredField: translate({ id: 'sms4.validations.requiredField' }),

    messageTooLong: (maxLength: number) =>
      translate({
        id: 'ui.hotWords.maxLengthExceeded',
        values: { maxLength },
      }),

    subjectTooLong: (maxLength: number) =>
      translate({
        id: 'sms4.validations.SmsSubjectTooLong',
        values: { maxLength },
      }),
  }))

  return api
}

export type Texts = ReturnType<typeof useTexts>
